<template>
    <div v-bind="$attrs" class="flex flex-col items-start gap-3 rounded-lg border border-border bg-surface-sunken px-4 py-4" data-test-id="profile-link-create-profile-prompt">
        <div class="flex flex-col gap-1">
            <p class="body-semibold text-ink text-center">{{ heading }}</p>
            <p class="body-small text-ink-muted leading-relaxed">
                {{ description }}<template v-if="profileLinkSharingEnabled">{{ ` ${sharingDisclosure}` }}</template>
                <BaseButton theme="tertiary" size="sm" class="w-fit" data-test-id="collaboration-profile-explainer-btn" @click="handleExplainerClick">
                    {{ t("insights.profileLinks.createPrompt.explainer") }}
                </BaseButton>
            </p>
        </div>
        <BaseButton theme="primary" class="w-full" @click="emit('cta-click')">{{ t("insights.profileLinks.createPrompt.createProfile") }}</BaseButton>
    </div>

    <DemoModal v-model:open="isArcadeOpen" :demo-url="COLLAB_PROFILE_DEMO_URL" :header-text="t('insights.profileLinks.createPrompt.explainer')" />
</template>

<script setup lang="ts">
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import BaseButton from "~vue/components/BaseButton.vue";
import DemoModal from "~vue/components/DemoModal.vue";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { sharingDisclosure } = useProfileSharingCopy();

const props = defineProps<{
    lockedSuggestionCount: number;
    profileLinkSharingEnabled: boolean;
}>();

const emit = defineEmits<{
    shown: [];
    "cta-click": [];
    "explainer-click": [];
}>();

// With sharing disabled org-wide, don't promise a "share" capability that doesn't exist —
// this becomes a plain profile-completion nudge instead.
const heading = computed(() => {
    if (!props.profileLinkSharingEnabled) {
        return t("insights.profileLinks.createPrompt.completeHeading");
    }
    return props.lockedSuggestionCount > 0
        ? t("insights.profileLinks.createPrompt.lockedHeading", { count: props.lockedSuggestionCount }, props.lockedSuggestionCount)
        : t("insights.profileLinks.createPrompt.shareHeading");
});

const description = computed(() =>
    props.profileLinkSharingEnabled ? t("insights.profileLinks.createPrompt.shareDescription") : t("insights.profileLinks.createPrompt.connectDescription"),
);

onMounted(() => emit("shown"));

const COLLAB_PROFILE_DEMO_URL = "https://demo.arcade.software/uz3KgHLOvPgO6XsujHWv?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true";
const isArcadeOpen = ref(false);

function handleExplainerClick() {
    isArcadeOpen.value = true;
    emit("explainer-click");
}
</script>
